import { Component, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { injectBrnDialogContext } from '@spartan-ng/brain/dialog';
import { HlmButtonImports } from '@spartan-ng/helm/button';
import { HlmInput } from '@spartan-ng/helm/input';
import { HlmLabel } from '@spartan-ng/helm/label';

export interface ConfirmData {
  title: string;
  messages: string[];
  resourceName?: string; // Optional: if provided, user must type first 4 chars to confirm
  /**
   * Whether to warn that the action cannot be undone. Defaults to `true`.
   *
   * Set it to `false` where that is not so — uninstalling a plugin, for one: the server marks the
   * entry uninstalled and keeps its configuration, and re-installing restores it. Telling somebody
   * their API keys are gone forever when they are not does not make them careful, it makes them
   * not do it.
   */
  irreversible?: boolean;
}

export interface OperationConfirmDialogContext extends ConfirmData {
  close: (result?: boolean) => void;
}

@Component({
  selector: 'app-operation-confirm-dialog',
  template: `
    <div class="p-6">
      <h2 class="text-lg font-semibold mb-4">
        {{ data.title || 'Confirm the operation' }}
      </h2>

      <div class="mb-4">
        @for (m of data.messages; track m) {
          <p class="mb-3">{{ m }}</p>
        }

        @if (data.resourceName) {
          <p class="mb-3 overflow-hidden text-ellipsis whitespace-nowrap">
            <strong>Resource: {{ data.resourceName }}</strong>
          </p>
          <div class="mt-2">
            <label class="mb-4" hlmLabel for="confirm-input">To confirm, type the first 4 characters of the name</label>
            <input
              hlmInput
              id="confirm-input"
              [(ngModel)]="userInput"
              placeholder="First 4 characters"
              autocomplete="off"
              class="w-full"
            />
          </div>
        }

        @if (irreversible()) {
          <p class="mb-0 mt-3">
            <strong>You cannot undo this.</strong>
          </p>
        }
      </div>

      <div class="flex justify-end gap-3">
        <button hlmBtn variant="outline" (click)="cancel()">Cancel</button>
        <button hlmBtn variant="destructive" [disabled]="!isConfirmEnabled()" (click)="confirm()">Confirm</button>
      </div>
    </div>
  `,
  imports: [HlmButtonImports, HlmLabel, HlmInput, FormsModule],
})
export class OperationConfirmDialogComponent {
  private context = injectBrnDialogContext<OperationConfirmDialogContext>();

  get data(): ConfirmData {
    return {
      title: this.context.title,
      messages: this.context.messages,
      resourceName: this.context.resourceName,
    };
  }

  /** Absent means yes: every existing caller relied on the warning being there. */
  irreversible = computed(() => this.context.irreversible !== false);

  userInput = signal('');

  isConfirmEnabled(): boolean {
    if (!this.data.resourceName) {
      return true; // No resource name required, always enabled
    }

    const resourceName = this.data.resourceName.toLowerCase();
    const minLength = Math.min(4, resourceName.length);
    const requiredPrefix = resourceName.substring(0, minLength);
    const typedInput = this.userInput().trim().toLowerCase();

    // Must match exactly the required prefix length
    return typedInput === requiredPrefix;
  }

  cancel(): void {
    this.context.close(false);
  }

  confirm(): void {
    if (this.isConfirmEnabled()) {
      this.context.close(true);
    }
  }
}
